import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ideiasApi } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Lightbulb,
  ThumbsUp,
  MessageSquareWarning,
  Sparkles,
  Plus,
  Search,
  Filter,
  Upload,
  X,
  Loader2,
  Clock,
  CheckCircle,
  AlertCircle,
  Play,
  Eye,
  MessageCircle,
  Send,
  Building2,
  User,
  Calendar,
  Image as ImageIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BancoIdeiasPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  
  const [loading, setLoading] = useState(false);
  const [ideias, setIdeias] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [busca, setBusca] = useState('');
  
  // Modals
  const [showNovaIdeiaModal, setShowNovaIdeiaModal] = useState(false);
  const [showDetalhesModal, setShowDetalhesModal] = useState(false);
  const [ideiaDetalhes, setIdeiaDetalhes] = useState(null);
  
  // Form
  const [novaIdeiaForm, setNovaIdeiaForm] = useState({
    tipo: 'sugestao',
    descricao: '',
    anexos: []
  });
  const [anexosPreview, setAnexosPreview] = useState([]);
  const [tratamentoComentario, setTratamentoComentario] = useState('');
  const [tratamentoStatus, setTratamentoStatus] = useState('');
  
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    loadData();
  }, [filtroTipo, filtroStatus]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroTipo && filtroTipo !== '__all__') params.tipo = filtroTipo;
      if (filtroStatus && filtroStatus !== '__all__') params.status = filtroStatus;
      
      const res = await ideiasApi.listar(params);
      setIdeias(res.data);
      
      if (isSuperAdmin) {
        try {
          const statsRes = await ideiasApi.resumo();
          setStats(statsRes.data);
        } catch (e) {
          console.log('Stats nao disponiveis');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar ideias:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`Arquivo ${file.name} muito grande (max 5MB)`);
        continue;
      }
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        
        try {
          setLoading(true);
          const res = await ideiasApi.uploadAnexo(base64);
          setNovaIdeiaForm(prev => ({
            ...prev,
            anexos: [...prev.anexos, res.data.url]
          }));
          setAnexosPreview(prev => [...prev, base64]);
          toast.success('Anexo enviado');
        } catch (error) {
          toast.error('Erro ao enviar anexo');
        } finally {
          setLoading(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  const removeAnexo = (index) => {
    setNovaIdeiaForm(prev => ({
      ...prev,
      anexos: prev.anexos.filter((_, i) => i !== index)
    }));
    setAnexosPreview(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleSubmitIdeia = async () => {
    if (!novaIdeiaForm.descricao.trim()) {
      toast.error('Descreva sua ideia');
      return;
    }
    
    setLoading(true);
    try {
      await ideiasApi.criar(novaIdeiaForm);
      toast.success('Ideia registrada com sucesso!');
      setShowNovaIdeiaModal(false);
      setNovaIdeiaForm({ tipo: 'sugestao', descricao: '', anexos: [] });
      setAnexosPreview([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar ideia');
    } finally {
      setLoading(false);
    }
  };
  
  const openDetalhes = async (ideia) => {
    setLoading(true);
    try {
      const res = await ideiasApi.obter(ideia.id);
      setIdeiaDetalhes(res.data);
      setShowDetalhesModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAdicionarTratamento = async () => {
    if (!tratamentoComentario.trim()) {
      toast.error('Digite um comentario');
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        comentario: tratamentoComentario,
        novo_status: tratamentoStatus || undefined
      };
      
      const res = await ideiasApi.adicionarTratamento(ideiaDetalhes.id, data);
      setIdeiaDetalhes(res.data);
      setTratamentoComentario('');
      setTratamentoStatus('');
      toast.success('Tratamento adicionado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar tratamento');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAtualizarStatus = async (novoStatus) => {
    setLoading(true);
    try {
      const res = await ideiasApi.atualizar(ideiaDetalhes.id, { status: novoStatus });
      setIdeiaDetalhes(res.data);
      toast.success('Status atualizado');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter ideias
  const ideiasFiltradas = ideias.filter(i => {
    if (busca && !i.descricao.toLowerCase().includes(busca.toLowerCase())) return false;
    return true;
  });
  
  // Config
  const tiposConfig = {
    elogio: { label: 'Elogio', icon: ThumbsUp, color: 'emerald' },
    reclamacao: { label: 'Reclamacao', icon: MessageSquareWarning, color: 'rose' },
    sugestao: { label: 'Sugestao', icon: Sparkles, color: 'amber' }
  };
  
  const statusConfig = {
    aberto: { label: 'Aberto', icon: AlertCircle, color: 'stone' },
    em_analise: { label: 'Em Analise', icon: Eye, color: 'blue' },
    em_andamento: { label: 'Em Andamento', icon: Play, color: 'amber' },
    concluido: { label: 'Concluido', icon: CheckCircle, color: 'emerald' }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-stone-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-stone-100" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Banco de Ideias
              </h1>
              <p className="text-stone-500 text-sm mt-1">
                Elogios, reclamacoes e sugestoes de melhoria
              </p>
            </div>
            
            <Button
              onClick={() => setShowNovaIdeiaModal(true)}
              className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold"
              data-testid="btn-nova-ideia"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Ideia
            </Button>
          </div>
          
          {/* Stats (Super Admin only) */}
          {isSuperAdmin && stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-stone-900/80 border border-stone-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Total</p>
                    <p className="text-2xl font-semibold text-stone-100 mt-1">{stats.total}</p>
                  </div>
                  <Lightbulb className="w-8 h-8 text-stone-700" />
                </div>
              </div>
              <div className={cn(
                "border rounded-lg p-4",
                stats.pendentes > 0 ? "bg-amber-500/10 border-amber-500/20" : "bg-stone-900/80 border-stone-800"
              )}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Pendentes</p>
                    <p className={cn("text-2xl font-semibold mt-1", stats.pendentes > 0 ? "text-amber-400" : "text-stone-100")}>
                      {stats.pendentes}
                    </p>
                  </div>
                  <Clock className={cn("w-8 h-8", stats.pendentes > 0 ? "text-amber-500/50" : "text-stone-700")} />
                </div>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Elogios</p>
                    <p className="text-2xl font-semibold text-emerald-400 mt-1">{stats.por_tipo?.elogio || 0}</p>
                  </div>
                  <ThumbsUp className="w-8 h-8 text-emerald-500/50" />
                </div>
              </div>
              <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Reclamacoes</p>
                    <p className="text-2xl font-semibold text-rose-400 mt-1">{stats.por_tipo?.reclamacao || 0}</p>
                  </div>
                  <MessageSquareWarning className="w-8 h-8 text-rose-500/50" />
                </div>
              </div>
            </div>
          )}
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <Input
                placeholder="Buscar..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-9 bg-stone-900 border-stone-800 text-stone-100"
                data-testid="input-busca-ideia"
              />
            </div>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-full sm:w-40 bg-stone-900 border-stone-800 text-stone-300">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent className="bg-stone-900 border-stone-700">
                <SelectItem value="__all__">Todos tipos</SelectItem>
                <SelectItem value="elogio">Elogio</SelectItem>
                <SelectItem value="reclamacao">Reclamacao</SelectItem>
                <SelectItem value="sugestao">Sugestao</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-full sm:w-40 bg-stone-900 border-stone-800 text-stone-300">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-stone-900 border-stone-700">
                <SelectItem value="__all__">Todos status</SelectItem>
                <SelectItem value="aberto">Aberto</SelectItem>
                <SelectItem value="em_analise">Em Analise</SelectItem>
                <SelectItem value="em_andamento">Em Andamento</SelectItem>
                <SelectItem value="concluido">Concluido</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Lista de Ideias */}
        <div className="space-y-3">
          {loading && ideias.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              Carregando...
            </div>
          ) : ideiasFiltradas.length === 0 ? (
            <div className="text-center py-16 bg-stone-900/30 border border-dashed border-stone-800 rounded-lg">
              <Lightbulb className="w-12 h-12 mx-auto text-stone-700 mb-4" />
              <p className="text-stone-400 mb-2">Nenhuma ideia encontrada</p>
              <p className="text-stone-500 text-sm">Seja o primeiro a registrar!</p>
            </div>
          ) : (
            ideiasFiltradas.map(ideia => {
              const tipoInfo = tiposConfig[ideia.tipo] || tiposConfig.sugestao;
              const statusInfo = statusConfig[ideia.status] || statusConfig.aberto;
              const TipoIcon = tipoInfo.icon;
              const StatusIcon = statusInfo.icon;
              
              return (
                <div
                  key={ideia.id}
                  onClick={() => openDetalhes(ideia)}
                  className="bg-stone-900/50 border border-stone-800 rounded-lg p-4 sm:p-5 hover:border-stone-700 transition-colors cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                      tipoInfo.color === 'emerald' && "bg-emerald-500/10 text-emerald-400",
                      tipoInfo.color === 'rose' && "bg-rose-500/10 text-rose-400",
                      tipoInfo.color === 'amber' && "bg-amber-500/10 text-amber-400",
                    )}>
                      <TipoIcon className="w-5 h-5" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={cn(
                          "text-xs px-2 py-0.5 rounded-full font-medium",
                          tipoInfo.color === 'emerald' && "bg-emerald-500/20 text-emerald-400",
                          tipoInfo.color === 'rose' && "bg-rose-500/20 text-rose-400",
                          tipoInfo.color === 'amber' && "bg-amber-500/20 text-amber-400",
                        )}>
                          {tipoInfo.label}
                        </span>
                        <span className={cn(
                          "text-xs px-2 py-0.5 rounded-full font-medium border",
                          statusInfo.color === 'stone' && "bg-stone-500/10 text-stone-400 border-stone-500/20",
                          statusInfo.color === 'blue' && "bg-blue-500/10 text-blue-400 border-blue-500/20",
                          statusInfo.color === 'amber' && "bg-amber-500/10 text-amber-400 border-amber-500/20",
                          statusInfo.color === 'emerald' && "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
                        )}>
                          <StatusIcon className="w-3 h-3 inline mr-1" />
                          {statusInfo.label}
                        </span>
                        {isSuperAdmin && ideia.company_nome && (
                          <span className="text-xs text-stone-500 flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {ideia.company_nome}
                          </span>
                        )}
                      </div>
                      
                      <p className="text-stone-100 text-sm line-clamp-2 mb-2">
                        {ideia.descricao}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-3 text-xs text-stone-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {ideia.created_by_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(ideia.created_at)}
                        </span>
                        {ideia.anexos?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <ImageIcon className="w-3 h-3" />
                            {ideia.anexos.length} anexo(s)
                          </span>
                        )}
                        {ideia.tratamentos?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {ideia.tratamentos.length} tratamento(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
      
      {/* Nova Ideia Modal */}
      <Dialog open={showNovaIdeiaModal} onOpenChange={setShowNovaIdeiaModal}>
        <DialogContent className="bg-stone-900 border-stone-700 shadow-2xl shadow-black/50 max-w-lg mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-stone-100">Registrar Ideia</DialogTitle>
            <DialogDescription className="text-stone-400">
              Compartilhe seu elogio, reclamacao ou sugestao
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-2 block">Tipo *</Label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(tiposConfig).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setNovaIdeiaForm(prev => ({ ...prev, tipo: key }))}
                      className={cn(
                        "p-3 rounded-lg border text-center transition-colors",
                        novaIdeiaForm.tipo === key
                          ? config.color === 'emerald' ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400"
                            : config.color === 'rose' ? "bg-rose-500/20 border-rose-500/50 text-rose-400"
                            : "bg-amber-500/20 border-amber-500/50 text-amber-400"
                          : "bg-stone-800/50 border-stone-700 text-stone-400 hover:border-stone-600"
                      )}
                    >
                      <Icon className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-xs">{config.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-1.5 block">Descricao *</Label>
              <Textarea
                value={novaIdeiaForm.descricao}
                onChange={(e) => setNovaIdeiaForm(prev => ({ ...prev, descricao: e.target.value }))}
                className="bg-stone-950 border-stone-800 text-stone-100 min-h-[120px]"
                placeholder="Descreva sua ideia em detalhes..."
                data-testid="textarea-descricao-ideia"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium text-stone-300 mb-2 block">Anexos (opcional)</Label>
              
              {anexosPreview.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {anexosPreview.map((preview, idx) => (
                    <div key={idx} className="relative group">
                      <img src={preview} alt={`Anexo ${idx + 1}`} className="w-16 h-16 object-cover rounded-lg border border-stone-700" />
                      <button
                        onClick={() => removeAnexo(idx)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="border-stone-700 text-stone-300 hover:bg-stone-800"
              >
                <Upload className="w-4 h-4 mr-2" />
                Adicionar print/anexo
              </Button>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setShowNovaIdeiaModal(false)} className="text-stone-300 hover:text-stone-100 hover:bg-stone-800">
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitIdeia}
              disabled={loading || !novaIdeiaForm.descricao.trim()}
              className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold"
              data-testid="btn-enviar-ideia"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Detalhes Modal */}
      <Dialog open={showDetalhesModal} onOpenChange={setShowDetalhesModal}>
        <DialogContent className="bg-stone-900 border-stone-700 shadow-2xl shadow-black/50 max-w-2xl mx-4 sm:mx-auto max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-stone-100">Detalhes da Ideia</DialogTitle>
          </DialogHeader>
          
          {ideiaDetalhes && (
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {/* Header info */}
              <div className="flex flex-wrap items-center gap-2">
                {(() => {
                  const tipoInfo = tiposConfig[ideiaDetalhes.tipo] || tiposConfig.sugestao;
                  const statusInfo = statusConfig[ideiaDetalhes.status] || statusConfig.aberto;
                  const TipoIcon = tipoInfo.icon;
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <>
                      <span className={cn(
                        "text-sm px-3 py-1 rounded-full font-medium flex items-center gap-1.5",
                        tipoInfo.color === 'emerald' && "bg-emerald-500/20 text-emerald-400",
                        tipoInfo.color === 'rose' && "bg-rose-500/20 text-rose-400",
                        tipoInfo.color === 'amber' && "bg-amber-500/20 text-amber-400",
                      )}>
                        <TipoIcon className="w-4 h-4" />
                        {tipoInfo.label}
                      </span>
                      <span className={cn(
                        "text-sm px-3 py-1 rounded-full font-medium border flex items-center gap-1.5",
                        statusInfo.color === 'stone' && "bg-stone-500/10 text-stone-400 border-stone-500/20",
                        statusInfo.color === 'blue' && "bg-blue-500/10 text-blue-400 border-blue-500/20",
                        statusInfo.color === 'amber' && "bg-amber-500/10 text-amber-400 border-amber-500/20",
                        statusInfo.color === 'emerald' && "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
                      )}>
                        <StatusIcon className="w-4 h-4" />
                        {statusInfo.label}
                      </span>
                    </>
                  );
                })()}
              </div>
              
              {/* Meta info */}
              <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-4 space-y-2 text-sm">
                {isSuperAdmin && ideiaDetalhes.company_nome && (
                  <div className="flex items-center gap-2 text-stone-400">
                    <Building2 className="w-4 h-4" />
                    <span className="text-stone-100">{ideiaDetalhes.company_nome}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-stone-400">
                  <User className="w-4 h-4" />
                  <span className="text-stone-100">{ideiaDetalhes.created_by_name}</span>
                </div>
                <div className="flex items-center gap-2 text-stone-400">
                  <Calendar className="w-4 h-4" />
                  <span className="text-stone-100">{formatDate(ideiaDetalhes.created_at)}</span>
                </div>
                {ideiaDetalhes.finalizado_at && (
                  <div className="flex items-center gap-2 text-stone-400">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className="text-emerald-400">Finalizado em {formatDate(ideiaDetalhes.finalizado_at)}</span>
                  </div>
                )}
              </div>
              
              {/* Descricao */}
              <div>
                <Label className="text-sm font-medium text-stone-300 mb-2 block">Descricao</Label>
                <p className="text-stone-100 bg-stone-800/30 border border-stone-800 rounded-lg p-4 whitespace-pre-wrap">
                  {ideiaDetalhes.descricao}
                </p>
              </div>
              
              {/* Anexos */}
              {ideiaDetalhes.anexos?.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-stone-300 mb-2 block">Anexos</Label>
                  <div className="flex flex-wrap gap-2">
                    {ideiaDetalhes.anexos.map((url, idx) => (
                      <a
                        key={idx}
                        href={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                          alt={`Anexo ${idx + 1}`}
                          className="w-24 h-24 object-cover rounded-lg border border-stone-700 hover:border-amber-500 transition-colors"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Tratamentos */}
              <div>
                <Label className="text-sm font-medium text-stone-300 mb-2 block">
                  Historico de Tratamento ({ideiaDetalhes.tratamentos?.length || 0})
                </Label>
                
                {ideiaDetalhes.tratamentos?.length > 0 ? (
                  <div className="space-y-3">
                    {ideiaDetalhes.tratamentos.map((t, idx) => (
                      <div key={t.id || idx} className="bg-stone-800/30 border border-stone-800 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-stone-100 text-sm font-medium">{t.created_by_name}</span>
                          <span className="text-stone-500 text-xs">{formatDate(t.created_at)}</span>
                        </div>
                        <p className="text-stone-300 text-sm whitespace-pre-wrap">{t.comentario}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-stone-500 text-sm">Nenhum tratamento registrado</p>
                )}
              </div>
              
              {/* Adicionar tratamento (Super Admin only) */}
              {isSuperAdmin && ideiaDetalhes.status !== 'concluido' && (
                <div className="border-t border-stone-800 pt-4">
                  <Label className="text-sm font-medium text-stone-300 mb-2 block">Adicionar Tratamento</Label>
                  <div className="space-y-3">
                    <Textarea
                      value={tratamentoComentario}
                      onChange={(e) => setTratamentoComentario(e.target.value)}
                      className="bg-stone-950 border-stone-800 text-stone-100"
                      placeholder="Digite seu comentario..."
                      rows={3}
                    />
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Select value={tratamentoStatus} onValueChange={setTratamentoStatus}>
                        <SelectTrigger className="w-full sm:w-48 bg-stone-950 border-stone-800 text-stone-300">
                          <SelectValue placeholder="Alterar status (opcional)" />
                        </SelectTrigger>
                        <SelectContent className="bg-stone-900 border-stone-700">
                          <SelectItem value="em_analise">Em Analise</SelectItem>
                          <SelectItem value="em_andamento">Em Andamento</SelectItem>
                          <SelectItem value="concluido">Concluido</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={handleAdicionarTratamento}
                        disabled={loading || !tratamentoComentario.trim()}
                        className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Enviar
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="border-t border-stone-800 pt-4">
            <Button variant="ghost" onClick={() => setShowDetalhesModal(false)} className="text-stone-300 hover:text-stone-100 hover:bg-stone-800">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BancoIdeiasPage;
